"use client"

import Image from "next/image"
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { useFocusTrap } from "../hooks/use-focus-trap"
import styles from "../styles/immersive-shop.module.css"
import { ImmersiveShopProduct } from "../types"

const MAX_VISIBLE_RESULTS = 48
const CLOSE_ANIMATION_MS = 420

function shouldAnimateClose() {
  if (typeof window === "undefined") {
    return false
  }

  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function normalizeSearchValue(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("it")
    .trim()
}

export default function ProductSearchOverlay({
  products,
  onClose,
  onSubmitSearch,
  onSelectProduct,
  scrollToTopOnSelect = false,
}: {
  products: ImmersiveShopProduct[]
  onClose: () => void
  onSubmitSearch: (
    query: string,
    matchingProducts: ImmersiveShopProduct[]
  ) => void
  onSelectProduct: (product: ImmersiveShopProduct) => void
  /**
   * Se true, quando l'utente sceglie un prodotto (o invia la ricerca) lo scroll
   * non viene ripristinato ma porta al top: serve quando la scelta apre una nuova
   * pagina. Chiudendo la ricerca senza scegliere, lo scroll viene sempre ripristinato.
   */
  scrollToTopOnSelect?: boolean
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const onCloseRef = useRef(onClose)
  const [query, setQuery] = useState("")
  const [isClosing, setIsClosing] = useState(false)
  const isClosingRef = useRef(false)
  const scrollToTopOnSelectRef = useRef(scrollToTopOnSelect)
  scrollToTopOnSelectRef.current = scrollToTopOnSelect
  const productSelectedRef = useRef(false)
  onCloseRef.current = onClose
  useFocusTrap(dialogRef, !isClosing)

  const normalizedQuery = normalizeSearchValue(query)
  const searchableProducts = useMemo(
    () =>
      products.map((product) => ({
        product,
        searchableText: normalizeSearchValue(
          `${product.title} ${product.handle} ${product.description}`
        ),
      })),
    [products]
  )
  const matchingProducts = useMemo(() => {
    if (!normalizedQuery) {
      return []
    }

    return searchableProducts
      .filter(({ searchableText }) => searchableText.includes(normalizedQuery))
      .map(({ product }) => product)
  }, [normalizedQuery, searchableProducts])
  const visibleResults = matchingProducts.slice(0, MAX_VISIBLE_RESULTS)

  const finishClose = useCallback(() => {
    isClosingRef.current = false
    setIsClosing(false)
    onCloseRef.current()
  }, [])

  const requestClose = useCallback(
    (action?: () => void) => {
      if (isClosingRef.current) {
        return
      }

      if (action) {
        productSelectedRef.current = true
      }

      if (!shouldAnimateClose()) {
        action?.()
        onCloseRef.current()
        return
      }

      isClosingRef.current = true
      setIsClosing(true)
      if (action) {
        startTransition(action)
      }
    },
    []
  )

  const submitSearch = () => {
    if (normalizedQuery && matchingProducts.length > 0) {
      const matching = matchingProducts
      const submittedQuery = query.trim()
      requestClose(() => onSubmitSearch(submittedQuery, matching))
    }
  }

  useEffect(() => {
    const scrollPosition = window.scrollY
    const previousBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      overscrollBehavior: document.body.style.overscrollBehavior,
    }
    const previousHtmlOverflow = document.documentElement.style.overflow
    const previousHtmlOverscroll =
      document.documentElement.style.overscrollBehavior

    document.body.style.position = "fixed"
    document.body.style.top = `-${scrollPosition}px`
    document.body.style.left = "0"
    document.body.style.right = "0"
    document.body.style.width = "100%"
    document.body.style.overflow = "hidden"
    document.body.style.overscrollBehavior = "none"
    document.documentElement.style.overflow = "hidden"
    document.documentElement.style.overscrollBehavior = "none"

    const focusFrame = window.requestAnimationFrame(() =>
      inputRef.current?.focus()
    )
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        requestClose()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.cancelAnimationFrame(focusFrame)
      window.removeEventListener("keydown", handleKeyDown)

      Object.assign(document.body.style, previousBodyStyles)
      document.documentElement.style.overflow = previousHtmlOverflow
      document.documentElement.style.overscrollBehavior =
        previousHtmlOverscroll
      window.scrollTo(
        0,
        scrollToTopOnSelectRef.current && productSelectedRef.current
          ? 0
          : scrollPosition
      )
    }
  }, [requestClose])

  useEffect(() => {
    if (!isClosing) {
      return
    }

    const timeout = window.setTimeout(() => {
      finishClose()
    }, CLOSE_ANIMATION_MS)

    return () => window.clearTimeout(timeout)
  }, [finishClose, isClosing])

  return (
    <div
      aria-label="Product search"
      aria-modal="true"
      className={`${styles.productSearchOverlay} ${
        isClosing ? styles.productSearchOverlayClosing : ""
      }`}
      onClick={() => requestClose()}
      ref={dialogRef}
      role="dialog"
      tabIndex={-1}
    >
      <form
        className={styles.productSearchPanel}
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault()
          submitSearch()
        }}
        role="search"
      >
        <button
          aria-label="Close search"
          className={styles.productSearchClose}
          onClick={() => requestClose()}
          type="button"
        >
          ×
        </button>

        <div className={styles.productSearchField}>
          <button
            aria-label="Cerca"
            className={styles.productSearchSubmit}
            disabled={!normalizedQuery || matchingProducts.length === 0}
            type="submit"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path d="m16.25 16.25 4 4" />
            </svg>
          </button>
          <input
            aria-label="Cerca un prodotto"
            autoComplete="off"
            enterKeyHint="search"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                event.preventDefault()
                submitSearch()
              }
            }}
            placeholder="what inspires you today?"
            ref={inputRef}
            type="search"
            value={query}
          />
        </div>

        {normalizedQuery ? (
          <div aria-live="polite" className={styles.productSearchResults}>
            {visibleResults.length > 0 ? (
              visibleResults.map((product) => {
                const imageUrl = product.imageUrls[0]

                return (
                  <button
                    className={styles.productSearchResult}
                    key={product.id}
                    onClick={() =>
                      requestClose(() => onSelectProduct(product))
                    }
                    type="button"
                  >
                    <span className={styles.productSearchResultImage}>
                      {imageUrl ? (
                        <Image
                          alt=""
                          draggable={false}
                          fill
                          loading="lazy"
                          quality={60}
                          sizes="(max-width: 800px) 42vw, 52px"
                          src={imageUrl}
                        />
                      ) : null}
                    </span>
                    <span className={styles.productSearchResultInfo}>
                      <strong>{product.title}</strong>
                      <small>
                        {product.price?.formatted ?? "Price unavailable"}
                      </small>
                    </span>
                    <span
                      aria-hidden="true"
                      className={styles.productSearchArrow}
                    >
                      ↗
                    </span>
                  </button>
                )
              })
            ) : (
              <p className={styles.productSearchEmpty}>
                No products found
              </p>
            )}
          </div>
        ) : null}
      </form>
    </div>
  )
}
